import { Banner, Tooltip } from '@mm/components';
import { PublicReplies } from '@mm/components/src/PublicReplies/PublicReplies';
import { useResolutionInfo } from '@mm/hooks';
import { InfoIcon } from '@mm/icons';
import { IGCommentGuardSlideoutBanner } from '@mm/icons/SlideoutBanners';
import { useInstagramCRUDActions } from '@mm/state/actions/InstagramCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { selectPromoterById } from '@mm/state/selectors/promoterSelector';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import React, { MutableRefObject, useEffect } from 'react';
import { useSelector } from 'react-redux';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import {
  BubbleTextInput,
  InputFieldRefType,
} from '../../../../../common/widgets/common/BubbleTextInput/BubbleTextInput';
import { DialoguePreview } from '../../../../../modules/DailoguePreview/DialoguePreview';
import {
  MOBILE_SMALL,
  MOBILE_THRESHOLD,
  TABLET,
} from '../../../../constants/screenSize';
import FrequencyCapControl from '../../../../features/startBotPromoters/commentGuards/components/CommentGuardForm/FrequencyCapControl';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import InstagramPostSelectionForm from '../../../../features/startBotPromoters/instagramCommentGuard/InstagramPostSelectionForm/InstagramPostSelectionForm';
import { useActivateIgCommentGuard } from '../../../../features/startBotPromoters/instagramCommentGuard/use-activate-ig-comment-guard';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './IGCommentGuardSlideoutEditor.module.css';

export interface IGCommentGuardSlideoutEditorProps {
  promoterId: number;
}

export const IGCommentGuardSlideoutEditor = ({
  promoterId,
}: IGCommentGuardSlideoutEditorProps) => {
  const chatbot = useSelector(activeChatbotSelector);
  const promoter = useSelector(state => selectPromoterById(state, promoterId));
  const page = useSelector(state =>
    pageEntityByIdSelector(state, String(promoter?.pageId)),
  );
  const fbPage = useSelector(activeFacebookPageSelector);
  const promoterCRUDActions = usePromoterCRUDActions();
  const pageCRUDActions = usePageCRUDActions();
  const widgetCRUDActions = useWidgetCRUDActions();
  const { customActivateAction, setToggler } =
    useActivateIgCommentGuard(promoter);
  const { getIGListMedia, getIGMedia, clearIGMedia } =
    useInstagramCRUDActions();
  const { screenWidth } = useResolutionInfo();
  const { setRef } = useSlideoutOnClose();
  const isMobile =
    screenWidth <= MOBILE_THRESHOLD && screenWidth > MOBILE_SMALL;
  const isTablet = screenWidth <= TABLET && screenWidth > MOBILE_THRESHOLD;
  const isMobileSmall = screenWidth <= MOBILE_SMALL;

  useEffect(() => {
    async function fetchItems() {
      await promoterCRUDActions.retrieveAll(
        chatbot.id,
        'instagram_comment_guard',
      );
    }
    fetchItems();
  }, [chatbot.id, promoterCRUDActions]);

  useEffect(() => {
    if (fbPage) {
      getIGListMedia(fbPage.id);
    }
  }, [fbPage?.id]);

  useEffect(() => {
    if (fbPage?.id && promoter?.postId) {
      if (['id', 'ALL'].includes(promoter?.postId)) {
        clearIGMedia();
      } else {
        getIGMedia(fbPage?.id, promoter?.postId);
      }
    }
  }, [promoter?.postId]);

  useEffect(() => {
    (async () => {
      if (page) {
        await Promise.all([
          pageCRUDActions.retrieveById(page.id),
          widgetCRUDActions.retrieveAll(page.id, true),
        ]);
      }
    })();
  }, [page?.id, pageCRUDActions, widgetCRUDActions]);

  if (!promoter || !page) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader
        promoter={promoter}
        customActivateAction={customActivateAction}
        title="Instagram Comment Autoresponder"
      />

      <FreeTrialWarning />

      <SlideoutMenu.Content className={styles.content}>
        {!isMobile && !isMobileSmall && (
          <Banner
            css={{
              collapsedHeader: styles.collapsedHeader,
              content: styles.bannerContent,
            }}
            withBannerHeader
            headerText="What is an Instagram Comment Autoresponder?"
            withLeftArea={false}
            showCloseIcon
            usedInSlideout
          >
            <IGCommentGuardSlideoutBanner textColor="white" />
          </Banner>
        )}
        <div className={styles.container}>
          <div className={styles.configArea}>
            <CollapsibleContentContainer
              orderNumber="1"
              containerName="Choose when to apply this comment autoresponder"
              className={styles.collapsibleContainer}
              stepperStylingPlatform="instagram"
              useCircleNumberStyle
            >
              <div className={styles.post_selection_block}>
                <div className={styles.post_selection_block_title}>
                  This Comment Autoresponder will reply to comments on ...
                </div>
                <InstagramPostSelectionForm
                  guard={promoter}
                  facebookPage={fbPage}
                  setToggler={setToggler}
                  keywordIputPlaceholder="e.g. “buy a home”, "
                  showKeywordsBroadMatch
                  customAddKeywordBtnName="Add"
                  keywordInfoIconColor="#262626"
                  customKeywordBroadPosition={
                    isTablet || isMobileSmall
                      ? 'top'
                      : isMobile
                      ? 'left'
                      : 'right'
                  }
                  customKeywordBroadAlign={isMobileSmall ? 'end' : 'center'}
                />
              </div>
            </CollapsibleContentContainer>

            <CollapsibleContentContainer
              orderNumber="2"
              containerName="Reply instantly to users who comment on your Post"
              className={styles.collapsibleContainer}
              stepperStylingPlatform="instagram"
              useCircleNumberStyle
            >
              <EmbeddableDialogueEditorWithPreview
                editPaneClassname={styles.dialogueEditPane}
                previewClassName={styles.dialogueEditPreviewPane}
                dialogueId={page.id}
                usedInSlideout
                hideDialogueSelector
                showAttributesStartWarning
                firstWidgetNotAllowedWarning={{
                  showWarning: true,
                  message: `This widget type is NOT supported as the first message in your Instagram Comment Autoresponder.
                   Please use a Quick Question widget instead.`,
                  startbotType: 'instagram_cg',
                }}
                platforms={['instagram']}
                disableFirstWidgetConditionalLogic
                forInstagram
              />
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber="3"
              containerName="Public Reply"
              className={styles.collapsibleContainerLast}
              stepperStylingPlatform="instagram"
              useCircleNumberStyle
            >
              <PublicReplies
                addReplyBtnText="Add one more"
                hideAddIcon
                css={{
                  addButtonStyle: styles.public_reply_add_btn,
                  deleteButtonIconStyle: styles.public_reply_delete_icon,
                }}
                deleteButtonIconColor="#2f54eb"
                isAutomationsSlideout
                createMultipleRepliesText={
                  <div className={styles.public_replies_multiple_text}>
                    <div className={styles.public_replies_multiple_text_title}>
                      Multiple replies
                    </div>
                    <div
                      className={
                        styles.public_replies_multiple_text_description
                      }
                    >
                      We’ll send them at random
                    </div>
                  </div>
                }
                customSendReply={
                  <div className={styles.public_reply_title}>
                    Send Public Reply
                    <Tooltip
                      placement={isMobile ? 'bottom' : 'right'}
                      title="Reply with a public comment on top-level user's comment.
                      Create multiple replies and we'll send them at random.
                      Hidden comments are not supported."
                    >
                      {' '}
                      <InfoIcon
                        className={styles.public_reply_title_info_icon}
                        color="#262626"
                      />
                    </Tooltip>
                  </div>
                }
                promoter={promoter}
                chatbotId={chatbot.id}
                customTextareaComponent={(
                  text: string,
                  onUpdate: (text: string) => void,
                  onClick: () => void,
                  maxLength: number,
                  editMode: boolean,
                  setEditMode: (edit: boolean) => void,
                  inputFieldRef: InputFieldRefType,
                  ref: MutableRefObject<HTMLDivElement | null>,
                  disabled?: boolean,
                ) => (
                  <BubbleTextInput
                    ref={ref}
                    value={text}
                    editMode={editMode}
                    setEditMode={setEditMode}
                    onClick={onClick}
                    updateText={onUpdate}
                    maxLength={maxLength}
                    showAttributePicker={false}
                    inputFieldRefToSet={inputFieldRef}
                    emojiPickerColor="#262626"
                    multiline={false}
                    className={styles.slideout_bubble_text_input}
                    isAutomationsSlideout
                    disabled={disabled}
                  />
                )}
              />
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber="4"
              containerName="Specify a Frequency Cap for Comment Replies"
              className={styles.collapsibleContainerLast}
              stepperStylingPlatform="instagram"
              useCircleNumberStyle
            >
              <FrequencyCapControl
                guard={promoter}
                chatbotId={chatbot.id}
                usedInSlideout
              />
            </CollapsibleContentContainer>
          </div>
          {!isMobile && (
            <DialoguePreview
              dialogueId={page.id}
              className={styles.dialoguePreview}
              sticky
            />
          )}
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
